
export interface DocumentSnapshot {
  content: string;
  cursorPosition: number;
  timestamp: number;
}

export class UndoRedoManager {
  private undoStack: DocumentSnapshot[] = [];
  private redoStack: DocumentSnapshot[] = [];
  private maxHistorySize = 50;
  private lastSaveTime = 0;
  private debounceDelay = 300; // Reduzido de 1000ms para 300ms para melhor responsividade

  saveSnapshot(content: string, cursorPosition: number = 0): void {
    const now = Date.now();
    
    // Debounce mais inteligente - permite snapshots mais frequentes durante edição ativa
    if (now - this.lastSaveTime < this.debounceDelay && this.undoStack.length > 0) {
      const lastSnapshot = this.undoStack[this.undoStack.length - 1];
      if (lastSnapshot && now - lastSnapshot.timestamp < this.debounceDelay) {
        // Só atualiza se o conteúdo realmente mudou
        if (lastSnapshot.content !== content) {
          lastSnapshot.content = content;
          lastSnapshot.cursorPosition = cursorPosition;
          lastSnapshot.timestamp = now;
          console.log('UndoRedo: Updated last snapshot');
        }
        return;
      }
    }

    const snapshot: DocumentSnapshot = {
      content,
      cursorPosition,
      timestamp: now
    };

    this.undoStack.push(snapshot);
    
    // Limita o tamanho do stack
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }

    // Limpa o redo stack quando nova ação é feita
    this.redoStack = [];
    this.lastSaveTime = now;

    console.log('UndoRedo: Snapshot saved', { 
      stackSize: this.undoStack.length, 
      contentLength: content.length 
    });
  }

  undo(): DocumentSnapshot | null {
    if (this.undoStack.length <= 1) {
      console.log('UndoRedo: Nothing to undo');
      return null;
    }

    // Move o snapshot atual para o redo stack
    const currentSnapshot = this.undoStack.pop();
    if (currentSnapshot) {
      this.redoStack.push(currentSnapshot);
    }

    // Retorna o snapshot anterior
    const previousSnapshot = this.undoStack[this.undoStack.length - 1];
    console.log('UndoRedo: Undo executed', { 
      undoSize: this.undoStack.length, 
      redoSize: this.redoStack.length,
      restoredContentLength: previousSnapshot?.content.length || 0
    });
    
    return previousSnapshot || null;
  }

  redo(): DocumentSnapshot | null {
    if (this.redoStack.length === 0) {
      console.log('UndoRedo: Nothing to redo');
      return null;
    }

    const redoSnapshot = this.redoStack.pop();
    if (redoSnapshot) {
      this.undoStack.push(redoSnapshot);
      console.log('UndoRedo: Redo executed', { 
        undoSize: this.undoStack.length, 
        redoSize: this.redoStack.length,
        restoredContentLength: redoSnapshot.content.length
      });
      return redoSnapshot;
    }

    return null;
  }

  canUndo(): boolean {
    return this.undoStack.length > 1;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    console.log('UndoRedo: History cleared');
  }

  forceSnapshot(content: string, cursorPosition: number = 0): void {
    // Força um snapshot imediatamente, ignorando debounce
    const snapshot: DocumentSnapshot = {
      content,
      cursorPosition,
      timestamp: Date.now()
    };

    this.undoStack.push(snapshot);
    
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }

    this.redoStack = [];
    console.log('UndoRedo: Forced snapshot saved', { 
      stackSize: this.undoStack.length,
      contentLength: content.length
    });
  }

  // Nova função para salvar snapshot antes de operações importantes
  saveSnapshotBeforeOperation(content: string, cursorPosition: number = 0): void {
    // Sempre salva um snapshot antes de operações importantes, ignorando debounce
    this.forceSnapshot(content, cursorPosition);
  }
}
